import * as React from 'react';
import { cn } from '@/hen-ui/utils';

export interface BrandCardContainerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The BrandCard components to display in the container.
   */
  children: React.ReactNode;

  /**
   * Custom class name for the container.
   */
  className?: string;
}

/**
 * BrandCardContainer - A layout wrapper for BrandCard components
 *
 * Provides a responsive multi-column layout for displaying multiple BrandCard
 * components with proper spacing and column behavior.
 *
 * ## Features
 * - Responsive columns: 1 column on mobile, 2 columns on tablet/desktop
 * - Automatic spacing between cards (36px mobile, 44px tablet, 60px desktop)
 * - CSS columns layout for natural content flow
 * - Each child wrapped to prevent column breaks
 *
 * @example
 * // Basic usage
 * <BrandCardContainer>
 *   <BrandCard {...props1} />
 *   <BrandCard {...props2} />
 *   <BrandCard {...props3} />
 * </BrandCardContainer>
 */
export const BrandCardContainer = React.forwardRef<
  HTMLDivElement,
  BrandCardContainerProps
>(({ children, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('columns-1 md:columns-2', className)}
      style={{ columnGap: '60px' }}
      {...props}
    >
      {React.Children.map(children, (child) => (
        <div className='mb-9 md:mb-11 lg:mb-15'>{child}</div>
      ))}
    </div>
  );
});

BrandCardContainer.displayName = 'BrandCardContainer';
